import * as THREE from 'three';
import { settings } from '@/config';
import type { TickState } from '@/types';
import type { Vec3 } from '@/types';

/** A god-rays volume's own position/rotation/scale — independent per instance, unlike
 *  the shared appearance settings (color/intensity/density/etc). */
export interface GodRaysTransform {
  position: Vec3;
  rotationDeg: Vec3;
  scale: Vec3;
}

/**
 * Volumetric "god rays" / light shafts — a STANDALONE box volume, independent of the
 * GLB (earlier exports sourced this from a `God_Rays_Mesh` cube, but tying a
 * volumetric effect to a specific Blender node name is fragile — it silently
 * disappears if that node is renamed or missing in a re-export). Its material is a
 * custom ADDITIVE shader that draws animated, fbm-broken light shafts travelling
 * along `rayDir`, in a warm yellow-gold — so it reads as sun rays streaming through.
 *
 * Multiple instances can share the SAME appearance (`settings.godRays.*` — color,
 * intensity, density, glow, softness, speed, rayDir) while each keeps its OWN
 * position/rotation/scale (`GodRaysTransform`, passed in per-instance) — so tweaking
 * the look in the debug GUI updates every volume at once, but placing one doesn't
 * move the others (2026-07-06, user: a second volume near `Pillar_S_2.002`, "make
 * sure god rays are consistent, when I change for one both get the same changes").
 *
 * The shafts are built in world space: the fragment's world position is split into a
 * component ALONG the ray and a PERPENDICULAR one; fbm over the perpendicular plane
 * (scrolled in time) carves the streaks, a soft core glow fills the beam, and a
 * view-facing (fresnel) term fades the mesh's silhouette so it never looks boxy.
 *
 * Rendered additively with depth test ON but depth write OFF, so the doors/arch occlude
 * the rays correctly without the rays z-fighting or hiding anything. Not a screen-space
 * scattering pass — it's localized to the volume, which is exactly what was asked for.
 */
export class GodRays {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private readonly rayDir = new THREE.Vector3();

  constructor(private readonly transform: GodRaysTransform) {
    const cfg = settings.godRays;
    this.rayDir.fromArray(cfg.rayDir).normalize();

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(cfg.color) },
        uIntensity: { value: cfg.intensity },
        uDensity: { value: cfg.density },
        uGlow: { value: cfg.glow },
        uRayDir: { value: this.rayDir.clone() },
        uSoftness: { value: cfg.softness },
      },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      fog: false,
    });

    // Unit cube — `settings.godRays.scale` sets its final world-space dimensions.
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.material);
    this.mesh.renderOrder = 2; // after opaque + water, so it adds on top
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.applyTransform();
  }

  update(tick: TickState): void {
    this.material.uniforms.uTime!.value += tick.delta * settings.godRays.speed;
  }

  /** Push live settings (colour, intensity, density, glow, direction) to the shader. */
  applySettings(): void {
    const cfg = settings.godRays;
    const u = this.material.uniforms;
    (u.uColor!.value as THREE.Color).set(cfg.color);
    u.uIntensity!.value = cfg.intensity;
    u.uDensity!.value = cfg.density;
    u.uGlow!.value = cfg.glow;
    u.uSoftness!.value = cfg.softness;
    this.rayDir.fromArray(cfg.rayDir).normalize();
    (u.uRayDir!.value as THREE.Vector3).copy(this.rayDir);
  }

  /** Push this instance's own live position/rotation/scale (degrees) to the volume mesh. */
  applyTransform(): void {
    const t = this.transform;
    this.mesh.position.fromArray(t.position);
    this.mesh.rotation.set(
      THREE.MathUtils.degToRad(t.rotationDeg[0]),
      THREE.MathUtils.degToRad(t.rotationDeg[1]),
      THREE.MathUtils.degToRad(t.rotationDeg[2]),
    );
    this.mesh.scale.fromArray(t.scale);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

const VERTEX_SHADER = /* glsl */ `
  varying vec3 vWorld;
  varying vec3 vNormalW;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    vNormalW = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec3 uColor;
  uniform float uIntensity;
  uniform float uDensity;
  uniform float uGlow;
  uniform vec3 uRayDir;
  uniform float uSoftness;

  varying vec3 vWorld;
  varying vec3 vNormalW;

  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 345.45));
    p += dot(p, p + 34.345);
    return fract(p.x * p.y);
  }
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * noise(p);
      p = p * 2.03 + 7.1;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 dir = normalize(uRayDir);

    // Orthonormal basis perpendicular to the ray → the "across the beam" plane.
    vec3 up = abs(dir.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 bu = normalize(cross(dir, up));
    vec3 bv = normalize(cross(dir, bu));

    float along = dot(vWorld, dir);
    vec3 perp = vWorld - dir * along;
    float cu = dot(perp, bu);
    float cv = dot(perp, bv);

    // Streaks: fbm across the beam, scrolled along the ray + in time → shimmering shafts.
    float n = fbm(vec2(cu, cv) * uDensity + vec2(along * 0.15, uTime));
    n += 0.5 * fbm(vec2(cu, cv) * uDensity * 2.3 - vec2(uTime * 0.7, along * 0.2));
    float shafts = pow(clamp(n * 0.6, 0.0, 1.0), 2.0);

    // Soft core glow across the beam so the volume reads as light, not just streaks.
    float core = exp(-length(vec2(cu, cv)) * (1.6 - uGlow));

    // Fresnel-ish silhouette fade so the cube edges melt instead of reading as a box.
    vec3 viewDir = normalize(cameraPosition - vWorld);
    float facing = pow(clamp(abs(dot(viewDir, vNormalW)), 0.0, 1.0), uSoftness);

    float energy = (shafts + core * uGlow) * facing * uIntensity;
    gl_FragColor = vec4(uColor * energy, energy);
  }
`;
